import React, { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Button,
  Box,
  Snackbar,
  Alert,
  Chip,
  Typography,
  Tab,
  Tabs,
  Badge,
} from '@mui/material'
import { Refresh as RefreshIcon } from '@material-ui/icons'
import {
  AdminPageWrapper,
  AdminTable,
  AdminTableColumn,
} from '../AdminComponents'
import { AiCoachingIntervention } from '../../../../Generated/graphql-types'
import {
  GET_AI_COACHING_INTERVENTIONS,
  CLAIM_AI_COACHING_INTERVENTION,
} from '../../../../Apollo/Queries/AiCoachingInterventions'
import { UserInfo } from './CoachingInterventions.styled'

type CoachingIntervention = AiCoachingIntervention

export const CoachingInterventions: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const [currentTab, setCurrentTab] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(50) // Default to 50, but allow users to change
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const pageSizeOptions = [25, 50, 100, 200] // Better options for large datasets

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(0) // Reset to first page when changing page size
  }

  // Separate queries for each tab
  const queryVariables = useMemo(() => {
    const statusFilter = (() => {
      switch (currentTab) {
        case 0: // New
          return { status: ['NEW'], unclaimed: true }
        case 1: // My Interventions
          return { claimedByMe: true }
        case 2: // Completed
          return {
            status: ['EMAILED', 'POSTED', 'POSTED_AND_EMAILED', 'DECLINED'],
          }
        default:
          return {}
      }
    })()

    return {
      ...statusFilter,
      limit: pageSize,
      offset: currentPage * pageSize,
    }
  }, [currentTab, currentPage, pageSize])

  const { data, loading, error, refetch } = useQuery<{
    aiCoachingInterventions: {
      total: number
      interventions: CoachingIntervention[]
    }
  }>(GET_AI_COACHING_INTERVENTIONS, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
  })

  // Count queries for badges
  const { data: newCount, refetch: refetchNewCount } = useQuery(
    GET_AI_COACHING_INTERVENTIONS,
    {
      variables: { status: ['NEW'], unclaimed: true, limit: 0 },
      fetchPolicy: 'cache-and-network',
    }
  )

  const { data: myCount, refetch: refetchMyCount } = useQuery(
    GET_AI_COACHING_INTERVENTIONS,
    {
      variables: { claimedByMe: true, limit: 0 },
      fetchPolicy: 'cache-and-network',
    }
  )

  const [claimIntervention] = useMutation(CLAIM_AI_COACHING_INTERVENTION)

  // Read tab from URL on mount and refetch data
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const tabFromUrl = searchParams.get('tab')
    if (tabFromUrl) {
      const tabIndex = parseInt(tabFromUrl, 10)
      if (!Number.isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 2) {
        setCurrentTab(tabIndex)
      }
    }
    // Refetch data when returning to this page
    if (refetch) refetch()
    if (refetchNewCount) refetchNewCount()
    if (refetchMyCount) refetchMyCount()
  }, [location.search, refetch, refetchNewCount, refetchMyCount])

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
    setCurrentPage(0)
    // Update URL with new tab
    history.replace(
      `/internal/self-serve/coaching-interventions?tab=${newValue}`
    )
  }

  const handleClaimIntervention = async (interventionId: number) => {
    try {
      const result = await claimIntervention({
        variables: { interventionId },
      })

      if (result.data?.claimAiCoachingIntervention?.success) {
        setSnackbar({
          open: true,
          message: 'Intervention claimed successfully!',
          severity: 'success',
        })
        // Refetch all queries to update counts and lists
        refetch()
        refetchNewCount()
        refetchMyCount()
      } else {
        setSnackbar({
          open: true,
          message:
            result.data?.claimAiCoachingIntervention?.message ||
            'Failed to claim intervention',
          severity: 'error',
        })
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error',
      })
    }
  }

  const handleRowClick = (row: CoachingIntervention) => {
    history.push(
      `/internal/self-serve/coaching-interventions/${row.id}?tab=${currentTab}`
    )
  }

  const getStatusColor = (
    status: string | null | undefined
  ): 'primary' | 'info' | 'success' | 'error' | 'default' => {
    switch (status) {
      case 'NEW':
        return 'primary'
      case 'EMAILED':
        return 'info'
      case 'POSTED':
        return 'success'
      case 'POSTED_AND_EMAILED':
        return 'success'
      case 'DECLINED':
        return 'error'
      default:
        return 'default'
    }
  }

  const columns: AdminTableColumn<CoachingIntervention>[] = [
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Chip
          label={(value || 'NEW').replace(/_/g, ' ')}
          color={getStatusColor(value)}
          size='small'
        />
      ),
    },
    {
      key: 'user',
      label: 'User',
      render: (_, row) => (
        <UserInfo>
          <Typography variant='body2'>
            {row.user?.firstName} {row.user?.lastName}
          </Typography>
          <Typography variant='caption' color='textSecondary'>
            {row.user?.email}
          </Typography>
        </UserInfo>
      ),
    },
    {
      key: 'identifier',
      label: 'Type',
      render: (value) => value.replace(/([A-Z])/g, ' $1').trim(),
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (value) =>
        new Date(value).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      key: 'claimedBy',
      label: 'Claimed By',
      render: (value) => (value ? `${value.firstName} ${value.lastName}` : '-'),
    },
  ]

  const newInterventionsColumns = columns.filter(
    (col) => col.key !== 'claimedBy'
  )

  const actions =
    currentTab === 0
      ? [
          {
            icon: undefined,
            tooltip: 'Claim this intervention',
            label: 'Claim',
            onClick: (row: CoachingIntervention) => {
              handleClaimIntervention(row.id || 0)
            },
            disabled: (row: CoachingIntervention) => !!row.claimedBy,
          },
        ]
      : []

  const getEmptyMessage = () => {
    if (error) return `Error loading interventions: ${error.message}`
    if (currentTab === 0) return 'No new interventions to review'
    if (currentTab === 1) return 'You have no claimed interventions'
    return 'No completed interventions'
  }

  return (
    <AdminPageWrapper
      title='Coaching Interventions'
      subtitle='Review and send AI-generated coaching messages to users'
      actions={
        <Button
          variant='outlined'
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      }
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab
            label={
              <Badge
                badgeContent={newCount?.aiCoachingInterventions?.total || 0}
                color='primary'
              >
                New Interventions
              </Badge>
            }
          />
          <Tab
            label={
              <Badge
                badgeContent={myCount?.aiCoachingInterventions?.total || 0}
                color='secondary'
              >
                My Interventions
              </Badge>
            }
          />
          <Tab label='Completed' />
        </Tabs>
      </Box>

      <AdminTable
        columns={currentTab === 0 ? newInterventionsColumns : columns}
        data={data?.aiCoachingInterventions?.interventions || []}
        loading={loading}
        onRowClick={handleRowClick}
        actions={actions}
        emptyMessage={getEmptyMessage()}
        pageSize={pageSize}
        totalCount={data?.aiCoachingInterventions?.total || 0}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
        serverSidePagination
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  )
}
